import { useEffect, useState } from "react";
import axios from "axios";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function LoginPosterPage() {
  const [portal, setPortal] = useState(null);
  const [link, setLink] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    API.get("/tenant/login-link")
      .then(({ data }) => {
        setLink(data);
        return axios.get(`${BACKEND}/api/public/tenant-portal/${encodeURIComponent(data.slug)}`);
      })
      .then((res) => { if (res) setPortal(res.data); })
      .catch((e) => setError(e.response?.data?.detail || "This workspace has no login link yet"));
  }, []);

  useEffect(() => {
    if (portal?.company_name) document.title = `${portal.company_name} — Sign-in poster`;
  }, [portal]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 text-center" data-testid="login-poster-error">
        <div>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <Button asChild variant="outline"><Link to="/platform/branding">Back to Branding</Link></Button>
        </div>
      </div>
    );
  }
  if (!portal || !link) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground" data-testid="login-poster-loading">Preparing your poster…</div>;
  }

  const display = link.url.replace(/^https?:\/\//, "");
  const accent = portal.primary_color || "#191A12";
  const logo = portal.logo_url ? (portal.logo_url.startsWith("http") ? portal.logo_url : `${BACKEND}${portal.logo_url}`) : "";
  const qr = `${BACKEND}${link.qr_path}?size=900`;

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white" data-testid="login-poster-page">
      <style>{`@page { size: A4 portrait; margin: 12mm; } @media print { .no-print { display: none !important; } }`}</style>

      <div className="no-print sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-slate-200 bg-white/90 px-6 py-3 backdrop-blur">
        <Button variant="ghost" size="sm" asChild><Link to="/platform/branding" data-testid="login-poster-back"><ArrowLeft size={14} className="mr-1" /> Branding</Link></Button>
        <p className="text-xs text-slate-500 hidden sm:block">A4 portrait · pin it in the office, van or site cabin</p>
        <Button size="sm" onClick={() => window.print()} style={{ backgroundColor: accent }} data-testid="login-poster-print">
          <Printer size={14} className="mr-1" /> Print poster
        </Button>
      </div>

      <div className="mx-auto flex max-w-[210mm] flex-col items-center px-6 py-10 print:px-0 print:py-0">
        <div
          className="relative w-full overflow-hidden rounded-2xl bg-white shadow-xl print:rounded-none print:shadow-none"
          style={{ minHeight: "270mm", borderTop: `14px solid ${accent}` }}
          data-testid="login-poster-sheet"
        >
          <div className="flex flex-col items-center px-12 pt-12 pb-10 text-center">
            {logo ? (
              <img src={logo} alt={portal.company_name} className="mb-6 max-h-24 max-w-[260px] object-contain" data-testid="login-poster-logo" />
            ) : (
              <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-2xl text-3xl font-bold text-white" style={{ backgroundColor: accent }}>
                {(portal.company_name || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <h1 className="font-[Chivo] text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl" data-testid="login-poster-title">
              Sign in to {portal.company_name}
            </h1>
            <p className="mt-3 max-w-md text-base text-slate-500">
              Scan the code with your phone camera to open our workspace, or type the address below.
            </p>

            <div className="mt-10 rounded-3xl border-8 bg-white p-4 shadow-sm" style={{ borderColor: accent }}>
              <img src={qr} alt={`QR code for ${display}`} className="h-[300px] w-[300px]" data-testid="login-poster-qr" />
            </div>

            <div className="mt-8 rounded-full px-8 py-3 font-mono text-xl font-semibold text-white" style={{ backgroundColor: accent }} data-testid="login-poster-url">
              {display}
            </div>

            <div className="mt-12 grid w-full max-w-lg grid-cols-3 gap-6 text-left text-sm text-slate-600">
              {[
                ["1", "Scan or type the address"],
                ["2", "Sign in with your work email"],
                ["3", "Add it to your home screen"],
              ].map(([n, txt]) => (
                <div key={n} className="flex items-start gap-2">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: accent }}>{n}</span>
                  <span>{txt}</span>
                </div>
              ))}
            </div>
          </div>

          {!portal.hide_platform_branding && (
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 border-t border-slate-100 py-4 text-xs text-slate-400" data-testid="login-poster-powered">
              <img src="/fixlr-icon.png" alt="" className="h-4 w-4 object-contain" /> Powered by Fixlr
            </div>
          )}
        </div>
        <p className="no-print mt-6 text-xs text-slate-400">Tip: choose A4 or Letter, portrait, and turn off headers/footers in the print dialog.</p>
      </div>
    </div>
  );
}
